import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "../i18n";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const rawReturnTo = queryParams.get("returnTo");

  // Only ever redirect to an in-app board path. Anything else (absolute URLs,
  // protocol-relative `//evil.com`, `javascript:`) falls back to the home page.
  let returnTo = rawReturnTo || "/";
  if (!returnTo.startsWith("/") || returnTo.startsWith("//")) {
    returnTo = "/";
  }
  if (!/^\/r\/[A-Za-z0-9]+$/.test(returnTo) && returnTo !== "/") {
    returnTo = "/";
  }

  const wasRedirected = returnTo !== "/";

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(username, password);
    setLoading(false);

    if (result.ok) {
      navigate(returnTo, { replace: true });
      return;
    }

    setError(
      result.reason === "provider_unavailable"
        ? t("login.providerUnavailable")
        : t("login.invalidCredentials")
    );
  };

  return (
    <div className="brand-wash flex min-h-[calc(100vh-16rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm animate-slideUp">
        <div className="mb-7 text-center">
          <h1 className="mb-1.5 text-2xl font-bold tracking-tight text-ink">
            {t("login.title")}
          </h1>
          <p className="text-sm text-ink-muted">
            {t("login.subtitle")}
          </p>
        </div>

        {wasRedirected && (
          <div className="mb-5 rounded-xl border border-nm-primary/20 bg-nm-primary/[0.06] px-4 py-3">
            <p className="text-sm text-ink-soft">
              {t("login.redirectedNotice")}
            </p>
          </div>
        )}

        <div className="card p-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label
                htmlFor="username"
                className="mb-1.5 block text-sm font-semibold text-ink-soft"
              >
                {t("login.username")}
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                placeholder={t("login.usernamePlaceholder")}
                className="input-field disabled:cursor-not-allowed disabled:opacity-60"
                autoComplete="username"
                autoFocus
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-sm font-semibold text-ink-soft"
              >
                {t("login.password")}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="••••••••"
                className="input-field disabled:cursor-not-allowed disabled:opacity-60"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="flex gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3"
              >
                <span aria-hidden="true" className="flex-shrink-0">
                  ⚠️
                </span>
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              className="btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {t("login.submitting")}
                </span>
              ) : (
                t("login.submit")
              )}
            </button>
          </form>
        </div>

        <p className="mt-5 text-center text-xs text-ink-faint">
          {t("login.anonymousHint")}
        </p>
      </div>
    </div>
  );
}
